/**
 * Single source of truth for every customer-facing preference option.
 *
 * Each option has a stable `key` (what we persist to the DB and send to the
 * fuel-plan engine) and a `label` (what shoppers see). Update labels freely;
 * never change a key without a migration plan — existing customer rows
 * reference these strings.
 *
 * The preference form, the engine payload mapping, and any future admin UI
 * should all import from here.
 */

export interface PreferenceOption {
  /** Stable identifier persisted to the DB / sent to the engine. */
  key: string;
  /** Human-facing label shown in the preferences form. */
  label: string;
  /** Optional explanatory hint shown alongside the label. */
  hint?: string;
}

/**
 * Hard dietary requirements — the engine must respect these (no swap will
 * ever serve gluten to a coeliac shopper). Persisted as a canonical-key list
 * in `CustomerPreferences.dietaryNeeds`.
 */
export const DIETARY_NEEDS: PreferenceOption[] = [
  { key: "glutenFree", label: "Gluten-Free" },
  { key: "vegan", label: "Vegan" },
  { key: "vegetarian", label: "Vegetarian" },
  { key: "noNuts", label: "No Nuts (including peanuts)" },
  { key: "dairyFree", label: "Dairy Free" },
  { key: "soyFree", label: "Soy Free" },
  { key: "noAddedCaffeine", label: "No Added Caffeine" }
];

/**
 * Soft dietary preferences — the engine should prefer these but it's OK to
 * include products that don't match them when the alternative is no plan at
 * all. Stored as boolean flags alongside the needs in `dietary`.
 */
export const DIETARY_PREFERENCES: PreferenceOption[] = [
  { key: "organic", label: "Organic" },
  { key: "lowSugar", label: "Low Sugar" },
  { key: "highCarb", label: "High Carb" },
  { key: "sugarFree", label: "Sugar Free" },
  { key: "kosher", label: "Kosher" },
  { key: "highProtein", label: "High Protein" },
  { key: "lowCarb", label: "Low Carb" },
  { key: "lowSodium", label: "Low Sodium" },
  { key: "caffeinated", label: "Caffeinated" }
];

/**
 * Combined view used when iterating dietary checkboxes for the form or when
 * normalising incoming form data. Order is preserved so the form layout
 * matches the brief.
 */
export const ALL_DIETARY: PreferenceOption[] = [...DIETARY_NEEDS, ...DIETARY_PREFERENCES];

/**
 * Product formats the engine can recommend. The shopper picks one set as a
 * positive preference and (optionally) another set as exclusions.
 */
export const FUEL_TYPES: PreferenceOption[] = [
  { key: "chews", label: "Chews" },
  { key: "gels", label: "Gels" },
  { key: "bars", label: "Bars" },
  { key: "stroopwafels", label: "Stroopwaffles" },
  { key: "hydration", label: "Hydration" },
  { key: "high_carb_drink", label: "High carb drink" },
  { key: "protein_mix", label: "Protein mix" },
  { key: "healthy_snacks", label: "Healthy snacks" },
  { key: "breakfast", label: "Breakfast" },
  { key: "wellness", label: "Wellness products" }
];

export const FLAVOR_PROFILES: PreferenceOption[] = [
  { key: "chocolate", label: "Chocolate" },
  { key: "vanilla", label: "Vanilla" },
  { key: "fruity", label: "Fruity" },
  { key: "citrus", label: "Citrus" },
  { key: "nutty", label: "Nutty", hint: "Peanut Butter, Almond" },
  { key: "caramelized", label: "Caramelized", hint: "Salted Caramel, Coffee, Maple" },
  { key: "spiced", label: "Spiced", hint: "Cinnamon, Chai" },
  { key: "unflavored", label: "Unflavored" }
];

/**
 * Safety cap on the number of flavor profiles a shopper can exclude. Marketing
 * removed the previous "max 2" UX limit (per Claude Design v4 — design says
 * "no limit on exclusions") but we keep a high server-side bound as a guard
 * against hostile inputs / form bugs. Tune freely; the engine still needs
 * SOME viable flavor space to recommend from.
 */
export const MAX_FLAVOR_EXCLUSIONS = 20;

/**
 * Brands shown in customer preferences (`/preferences` API catalog). Keep this list
 * aligned with what the storefront actually sells — keys persist on `CustomerPreferences`
 * and Shopify metafields, so rename keys carefully (migration needed).
 */
export const BRANDS: PreferenceOption[] = [
  { key: "carbs_fuel", label: "Carbs Fuel" },
  { key: "gu_energy", label: "GU" },
  { key: "honey_stinger", label: "Honey Stinger" },
  { key: "huma", label: "Huma" },
  { key: "insaltd", label: "Insalt'd" },
  { key: "maurten", label: "Maurten" },
  { key: "noogs", label: "Noogs" },
  { key: "precision_fh", label: "Precision F&H" },
  { key: "rnwy", label: "RNWY" },
  { key: "saltstick", label: "SaltStick" },
  { key: "sis", label: "SiS" },
  { key: "skratch_labs", label: "Skratch Labs" },
  { key: "tailwind", label: "Tailwind" },
  { key: "untapped", label: "Untapped" }
];

/* ─── Hidden-preference contract ───────────────────────────────────────────
 *
 * Some option keys stay valid (DB rows + Shopify metafields reference them, and
 * `knownOnly()` on save must keep accepting them) but are suppressed from one or
 * more layers. The three layers are independent:
 *
 *   UI hidden     — not rendered on customer-facing preference surfaces (modal
 *                   cards/chips + dashboard summary). Still hydrated into form
 *                   state and re-emitted on save, so editing never clears them.
 *   Outbound excl — stripped from the external fuel-plan engine payload
 *                   (`buildOutboundPayload`), so the engine never sees them.
 *
 * Persistence is always preserved — nothing here removes a key from the DB or
 * Shopify; the server-side merge on save (v3Routes) re-injects UI-hidden keys
 * the client never sends back.
 *
 * NEVER delete the underlying keys from DIETARY_PREFERENCES / FUEL_TYPES /
 * BRANDS — hide via these sets only.
 */

/** Dietary-style prefs hidden from customer-facing UI; still persisted + synced to Shopify. */
export const UI_HIDDEN_DIETARY_PREFERENCE_KEYS = new Set(["lowSugar", "sugarFree"]);

/** Dietary-style prefs omitted from the outbound fuel-plan engine payload. */
export const OUTBOUND_EXCLUDED_DIETARY_PREFERENCE_KEYS = new Set(["lowSugar", "sugarFree"]);

/** Fuel-type formats hidden from the preferences modal; still persisted + synced. */
export const UI_HIDDEN_FUEL_TYPE_KEYS = new Set(["bars", "healthy_snacks", "breakfast"]);

/** Fuel-type formats omitted from the outbound engine payload (same set as UI-hidden). */
export const OUTBOUND_EXCLUDED_FUEL_TYPE_KEYS = UI_HIDDEN_FUEL_TYPE_KEYS;

/**
 * Keep only the string entries of `values` that aren't in `exclude`. Shared by
 * the summary surfaces (drop UI-hidden keys before label lookup) and the
 * outbound payload (drop outbound-excluded keys before `resolveLabels`).
 */
export function filterPreferenceKeys(values: unknown, exclude: Set<string>): string[] {
  if (!Array.isArray(values)) return [];
  return values.filter(
    (v): v is string => typeof v === "string" && v.length > 0 && !exclude.has(v)
  );
}

/**
 * Quick map from key → label for rendering chosen values back into the
 * dashboard summary or any read-only views without re-iterating the lists.
 */
export function buildLabelMap(options: PreferenceOption[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const o of options) map[o.key] = o.label;
  return map;
}

export const FUEL_TYPE_LABELS = buildLabelMap(FUEL_TYPES);
export const FLAVOR_LABELS = buildLabelMap(FLAVOR_PROFILES);
export const BRAND_LABELS = buildLabelMap(BRANDS);
export const DIETARY_LABELS = buildLabelMap(ALL_DIETARY);

/**
 * Return only the options whose keys appear in `selected`. Used to render a
 * compact summary on the dashboard ("Vegan · Gluten-Free · High Protein").
 */
export function labelsFor(options: PreferenceOption[], selected: string[]): string[] {
  const set = new Set(selected);
  return options.filter((o) => set.has(o.key)).map((o) => o.label);
}
